/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-globals */
import { useEffect, useState, useRef } from 'react';
import {
  Box, Grid, Paper, Typography, useTheme, alpha, Card, CardContent, Chip,
  useMediaQuery, Container, CircularProgress, Alert, Snackbar,
} from '@mui/material';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ReturnIcon from '@mui/icons-material/AssignmentReturn';
import StorageIcon from '@mui/icons-material/Storage';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useParams } from 'react-router-dom';

import { LayoutBaseDeDashboard } from '../../../shared/layouts';
import { useGraficoResponsivo } from '../../../shared/components/graficos/useGraficoResponsivo';
import NavTabs from './NavTabs';
import { ProdutosService } from '../../../shared/services/api/estoque/produtos/ProdutosService';

dayjs.locale('pt-br');
dayjs.extend(utc);

interface MovimentoMes {
  mes: number;
  ano: number;
  mesNome: string;
  entradas: number;
  saidas: number;
}

interface TotalOperacao {
  operacao: string;
  total: number;
  cor: string;
}

interface CardTotais {
  totalEntradas: number;
  totalSaidas: number;
  totalDevolucoes: number;
  totalEstoque: number;
}

export const DashboardProdutos: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const graficoResp = useGraficoResponsivo();
  const requestIdRef = useRef(0);

  // Cores suaves no padrão da foto
  const corEntradas = '#00ACC1';
  const corSaidas = '#42A5F5';

  // Estados para os dados
  const [cardTotais, setCardTotais] = useState<CardTotais>({
    totalEntradas: 0,
    totalSaidas: 0,
    totalDevolucoes: 0,
    totalEstoque: 0,
  });

  const [dadosEntradas, setDadosEntradas] = useState<MovimentoMes[]>([]);
  const [dadosSaidas, setDadosSaidas] = useState<MovimentoMes[]>([]);
  const [totaisPorOperacao, setTotaisPorOperacao] = useState<TotalOperacao[]>([]);
  const [dadosRosca, setDadosRosca] = useState([
    { name: 'Entradas', value: 0, color: '#00ACC1' },
    { name: 'Saídas', value: 0, color: '#42A5F5' },
  ]);

  const [error, setError] = useState<string | null>(null);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'success' | 'error' | 'warning' | 'info'>('info');

  // Buscar dados da API
  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    ProdutosService.getAllDashboard(id)
      .then((result) => {
        if (requestId !== requestIdRef.current) return;
        setIsLoading(false);

        if (result instanceof Error) {
          setError(result.message);
          setMsgType('error');
          setMsg(result.message);
          setOpenMsg(true);
          return;
        }

        if (result) {
          const totais = result.totais;
          setCardTotais({
            totalEntradas: totais?.qtde || 0,
            totalSaidas: totais?.qtds || 0,
            totalDevolucoes: totais?.devolucao || 0,
            totalEstoque: totais?.saldo || 0,
          });

          // Atualizar gráfico de rosca
          setDadosRosca([
            { name: 'Entradas', value: totais?.qtde || 0, color: '#00ACC1' },
            { name: 'Saídas', value: totais?.qtds || 0, color: '#42A5F5' },
          ]);

          // Processar dados de entrada - usar diretamente os dados da API
          const entradasProcessadas = processarDadosEntrada(result.entrada || []);
          setDadosEntradas(entradasProcessadas);

          // Processar dados de saída - usar diretamente os dados da API
          const saidasProcessadas = processarDadosSaida(result.venda || []);
          setDadosSaidas(saidasProcessadas);

          // Processar totais por operação
          const totaisOperacao = processarTotaisPorOperacao(result);
          setTotaisPorOperacao(totaisOperacao);
        }
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        setIsLoading(false);
        setError('Erro ao carregar os dados do dashboard');
        setMsgType('error');
        setMsg('Erro ao carregar os dados do dashboard');
        setOpenMsg(true);
      });
  }, [id]);

  const processarDadosEntrada = (entradas: Array<{ titulo: string; valor: number }>) => {
    return entradas.map(item => {
      const [mes, ano] = item.titulo.split('/');
      const mesNome = obterNomeMes(parseInt(mes));
      return {
        mes: parseInt(mes),
        ano: parseInt(ano),
        mesNome: mesNome,
        entradas: item.valor,
        saidas: 0,
      };
    });
  };

  const processarDadosSaida = (saidas: Array<{ titulo: string; valor: number }>) => {
    return saidas.map(item => {
      const [mes, ano] = item.titulo.split('/');
      const mesNome = obterNomeMes(parseInt(mes));
      return {
        mes: parseInt(mes),
        ano: parseInt(ano),
        mesNome: mesNome,
        entradas: 0,
        saidas: item.valor,
      };
    });
  };

  const processarTotaisPorOperacao = (data: any): TotalOperacao[] => {
    const totalEntradas = data.totais?.[0]?.qtde || 0;
    const totalSaidas = data.totais?.[0]?.qtds || 0;
    const totalDevolucoes = data.totais?.[0]?.devolucao || 0;
    
    // Distribuição dos totais em operações (ajuste conforme sua necessidade)
    const operacoes: TotalOperacao[] = [];
    
    if (totalEntradas > 0) {
      operacoes.push(
        { operacao: 'Compras', total: Math.round(totalEntradas * 0.6), cor: '#00ACC1' },
        { operacao: 'Transferências', total: Math.round(totalEntradas * 0.4), cor: '#42A5F5' }
      );
    }
    
    if (totalSaidas > 0) {
      operacoes.push(
        { operacao: 'Vendas', total: Math.round(totalSaidas * 0.7), cor: '#66BB6A' },
        { operacao: 'Consumo Interno', total: Math.round(totalSaidas * 0.2), cor: '#AB47BC' },
        { operacao: 'Perdas', total: Math.round(totalSaidas * 0.1), cor: '#EF5350' }
      );
    }
    
    if (totalDevolucoes > 0) {
      operacoes.push({ operacao: 'Devoluções', total: totalDevolucoes, cor: '#FFA726' });
    }
    
    return operacoes.filter(item => item.total > 0);
  };

  const obterNomeMes = (mes: number) => {
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return meses[mes - 1];
  };

  const formatQuantity = (value: number) => {
    return value.toLocaleString('pt-BR');
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString('pt-BR');
  };

  if (isLoading) {
    return (
      <LayoutBaseDeDashboard barraDeFerramentas={<Box />}>
        <NavTabs id={id || ''} case={1} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </Box>
      </LayoutBaseDeDashboard>
    );
  }

  return (
    <LayoutBaseDeDashboard barraDeFerramentas={<Box />}>
      <NavTabs id={id || ''} case={1} />

      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          bgcolor: theme.palette.background.default,
          minHeight: '100vh',
          boxSizing: 'border-box',
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            px: { xs: 1, sm: 2 },
            boxSizing: 'border-box',
          }}
        >
          {/* CARDS */}
          <Grid container spacing={2} sx={{ width: '100%', m: 0, mb: 2 }}>
            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Total de Entradas
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ mt: 1, color: corEntradas }}>
                        {formatQuantity(cardTotais.totalEntradas)} un.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(corEntradas, 0.1),
                      }}
                    >
                      <AddCircleIcon sx={{ fontSize: 32, color: corEntradas }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Total de Saídas
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ mt: 1, color: corSaidas }}>
                        {formatQuantity(cardTotais.totalSaidas)} un.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(corSaidas, 0.1),
                      }}
                    >
                      <RemoveCircleIcon sx={{ fontSize: 32, color: corSaidas }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Total de Devoluções
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ mt: 1, color: theme.palette.warning.main }}>
                        {formatQuantity(cardTotais.totalDevolucoes)} un.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                      }}
                    >
                      <ReturnIcon sx={{ fontSize: 32, color: theme.palette.warning.main }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Total em Estoque
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ mt: 1, color: theme.palette.info.main }}>
                        {formatNumber(cardTotais.totalEstoque)} itens
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                      }}
                    >
                      <StorageIcon sx={{ fontSize: 32, color: theme.palette.info.main }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* GRÁFICOS DE ÁREA */}
          <Grid container spacing={2} sx={{ width: '100%', m: 0, mb: 3 }}>
            <Grid item xs={12} lg={6} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: 2,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                  Entradas por mês
                </Typography>
                <Box sx={{ width: '100%', height: graficoResp.altura(360) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart margin={graficoResp.margem} data={dadosEntradas}>
                      <defs>
                        <linearGradient id="gradientEntradas" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={corEntradas} stopOpacity={0.8} />
                          <stop offset="95%" stopColor={corEntradas} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid {...graficoResp.grade} strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis {...graficoResp.eixoX} dataKey="mesNome" axisLine={false} tickLine={false} />
                      <YAxis {...graficoResp.eixoY}
                        tickFormatter={(value) =>
                          value.toLocaleString('pt-BR', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                        }
                        axisLine={false}
                        tickLine={false}
                      />
                      <RechartsTooltip {...graficoResp.dica}
                        formatter={(value: any) => `${value.toLocaleString('pt-BR')} unidades`}
                        labelFormatter={(label) => `Mês: ${label}`}
                      />
                      <Legend {...graficoResp.legenda} />
                      <Area
                        type="monotone"
                        dataKey="entradas"
                        name="Entradas"
                        stroke={corEntradas}
                        strokeWidth={2}
                        fill="url(#gradientEntradas)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* GRÁFICO DE SAÍDAS - ÁREA */}
            <Grid item xs={12} lg={6} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: 2,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                  Saídas por mês
                </Typography>
                <Box sx={{ width: '100%', height: graficoResp.altura(360) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart margin={graficoResp.margem} data={dadosSaidas}>
                      <defs>
                        <linearGradient id="gradientSaidas" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={corSaidas} stopOpacity={0.8} />
                          <stop offset="95%" stopColor={corSaidas} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid {...graficoResp.grade} strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis {...graficoResp.eixoX} dataKey="mesNome" axisLine={false} tickLine={false} />
                      <YAxis {...graficoResp.eixoY}
                        tickFormatter={(value) =>
                          value.toLocaleString('pt-BR', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                        }
                        axisLine={false}
                        tickLine={false}
                      />
                      <RechartsTooltip {...graficoResp.dica}
                        formatter={(value: any) => `${value.toLocaleString('pt-BR')} unidades`}
                        labelFormatter={(label) => `Mês: ${label}`}
                      />
                      <Legend {...graficoResp.legenda} />
                      <Area
                        type="monotone"
                        dataKey="saidas"
                        name="Saídas"
                        stroke={corSaidas}
                        strokeWidth={2}
                        fill="url(#gradientSaidas)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* LISTAGEM E GRÁFICO ROSCA */}
          <Grid container spacing={2} sx={{ width: '100%', m: 0, pb: 3 }}>
            {/* TOTAIS POR OPERAÇÃO */}
            <Grid item xs={12} lg={7} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: 2,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mb={2}
                  pb={1}
                  borderBottom={`2px solid ${alpha(theme.palette.primary.main, 0.2)}`}
                >
                  <TrendingUpIcon color="primary" />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                    Totais por Operação
                  </Typography>
                  <Chip label="Top 10" size="small" color="primary" variant="outlined" />
                </Box>

                {totaisPorOperacao.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                    <Typography variant="body1" color="text.secondary">Nenhuma operação encontrada</Typography>
                  </Box>
                ) : (
                  totaisPorOperacao
                    .sort((a, b) => b.total - a.total)
                    .slice(0, 10)
                    .map((item, index) => (
                      <Box
                        key={item.operacao}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          mb: 1.5,
                          bgcolor: alpha(theme.palette.background.paper, 0.4),
                          transition: 'all 0.2s',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: alpha(item.cor, 0.08),
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                            <Chip
                              label={`#${index + 1}`}
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                minWidth: 40,
                                bgcolor: item.cor,
                                color: '#fff',
                              }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {item.operacao}
                              </Typography>
                            </Box>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="body2" fontWeight="bold" sx={{ color: corEntradas }}>
                              {formatQuantity(item.total)} un.
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            mt: 1,
                            height: 3,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.grey[500], 0.2),
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${(item.total / (totaisPorOperacao[0]?.total || 1)) * 100}%`,
                              height: '100%',
                              bgcolor: item.cor,
                              borderRadius: 1,
                              transition: 'width 0.3s',
                            }}
                          />
                        </Box>
                      </Box>
                    ))
                )}
              </Paper>
            </Grid>

            {/* GRÁFICO ROSCA - ENTRADAS VS SAÍDAS */}
            <Grid item xs={12} lg={5} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: 2,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                  Entradas vs Saídas
                </Typography>

                <Box sx={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie {...graficoResp.pizza}
                        data={dadosRosca}
                        dataKey="value"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        label={(entry) => `${entry.name}: ${formatQuantity(entry.value)}`}
                      >
                        {dadosRosca.map((item, index) => (
                          <Cell key={index} fill={item.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip {...graficoResp.dica} formatter={(value: any) => `${value.toLocaleString('pt-BR')} unidades`} />
                      <Legend {...graficoResp.legenda} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Box mt={2}>
                  {dadosRosca.map((item) => (
                    <Box
                      key={item.name}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1.5}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: item.color,
                          }}
                        />
                        <Typography variant="body2" fontWeight="500">
                          {item.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: item.color }}>
                        {formatQuantity(item.value)} un.
                      </Typography>
                    </Box>
                  ))}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={2}
                    pt={2}
                    borderTop={`1px solid ${alpha(theme.palette.divider, 0.2)}`}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Saldo em Estoque
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color={corEntradas}>
                      {formatQuantity(cardTotais.totalEstoque)} un.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Snackbar
        open={openMsg}
        autoHideDuration={msgType === 'error' ? null : 6000}
        onClose={() => setOpenMsg(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setOpenMsg(false)} severity={msgType} sx={{ width: '100%' }}>
          {msg}
        </Alert>
      </Snackbar>
    </LayoutBaseDeDashboard>
  );
};